/* ContactsView holds all contacts-related stuff */
import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { Drafty, Kaltiq } from 'kaltiq-sdk';

import ContactList from '../widgets/contact-list.jsx';

import { updateFavicon } from '../lib/utils.js';

const messages = defineMessages({
  archived_contacts_title: {
    id: "archived_contacts",
    defaultMessage: "Archived contacts ({count})",
    description: "Label for archived chats"
  }
});

export default class ContactsView extends React.Component {
  constructor(props) {
    super(props);

    this.handleAction = this.handleAction.bind(this);
    this.handleTopicSelected = this.handleTopicSelected.bind(this);
    this.markAllRead = this.markAllRead.bind(this);
    this.deleteSelected = this.deleteSelected.bind(this);

    this.state = Object.assign(ContactsView.deriveStateFromProps(props, 'messages'), {
      activeTab: 'messages', menuOpen: false, selecting: false, selected: []
    });
  }

  static deriveStateFromProps(props, activeTab) {
    const contacts = [];
    let unreadThreads = 0;
    let archivedCount = 0;

    const me = props.kaltiq.getMeTopic();
    props.chatList.forEach(c => {
      const blocked = c.acs && !c.acs.isJoiner();
      c.pinned = me.pinnedTopicRank(c.topic);

      // Show only blocked contacts only when props.blocked == true.
      if (blocked && props.blocked) {
        contacts.push(c);
      }
      if (blocked || props.blocked) {
        return;
      }

      if (c.private && c.private.arch) {
        if (props.archive) {
          contacts.push(c);
        } else {
          archivedCount ++;
        }
      } else if (!props.archive && ContactsView.matchesTab(props.kaltiq, c, activeTab)) {
        contacts.push(c);
        unreadThreads += c.unread > 0 ? 1 : 0;
      }
    });

    contacts.sort((a, b) => {
      const pin = b.pinned - a.pinned;
      return pin != 0 ? pin : (b.touched || 0) - (a.touched || 0);
    });

    if (archivedCount > 0) {
      contacts.push({
        action: 'archive',
        title: messages.archived_contacts_title,
        values: {count: archivedCount}
      });
    }

    return {
      contactList: contacts,
      unreadThreads: unreadThreads
    };
  }

  static matchesTab(kaltiq, contact, activeTab) {
    if (!activeTab || activeTab === 'messages') {
      return Kaltiq.isP2PTopicName(contact.topic);
    }
    if (activeTab === 'channels') {
      return Kaltiq.isChannelTopicName(contact.topic);
    }
    if (activeTab === 'groups') {
      return Kaltiq.isGroupTopicName(contact.topic) && !Kaltiq.isChannelTopicName(contact.topic);
    }
    if (activeTab === 'contacts') {
      return Kaltiq.isP2PTopicName(contact.topic);
    }
    const topic = kaltiq.getTopic(contact.topic);
    if (activeTab === 'calls') {
      let found = false;
      topic?.messages(msg => { if (msg.head && msg.head.webrtc) found = true; });
      return found;
    }
    if (activeTab === 'updates') {
      const tags = topic?.tags?.() || [];
      return contact.public?.type === 'status' || tags.includes('kaltiq:status');
    }
    return true;
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.chatList != this.props.chatList ||
        prevProps.archive != this.props.archive ||
        prevProps.blocked != this.props.blocked) {
      const newState = ContactsView.deriveStateFromProps(this.props, this.state.activeTab);
      this.setState(newState);
      if (newState.unreadThreads != prevState.unreadThreads) {
        updateFavicon(newState.unreadThreads);
      }
    }
  }

  handleAction(action_ignored) {
    this.props.onShowArchive();
  }

  selectTab(activeTab) {
    this.setState(Object.assign(ContactsView.deriveStateFromProps(this.props, activeTab), {
      activeTab, selecting: false, selected: [], menuOpen: false
    }));
  }

  handleTopicSelected(topicName) {
    if (!this.state.selecting) {
      this.props.onTopicSelected(topicName);
      return;
    }
    const selected = this.state.selected.includes(topicName) ?
      this.state.selected.filter(name => name !== topicName) : [...this.state.selected, topicName];
    this.setState({selected});
  }

  markAllRead() {
    this.props.chatList.forEach(c => {
      const topic = this.props.kaltiq.getTopic(c.topic);
      if (topic && c.unread > 0) topic.noteRead(topic.seq || c.seq || 0);
    });
    this.setState({menuOpen: false});
  }

  deleteSelected() {
    if (this.state.selected.length === 0 || !window.confirm(`Delete ${this.state.selected.length} selected chat(s)? This cannot be undone.`)) {
      return;
    }
    Promise.all(this.state.selected.map(name => this.props.kaltiq.getTopic(name)?.delTopic(true)))
      .then(() => this.setState({selecting: false, selected: [], menuOpen: false}))
      .catch(err => this.props.onError?.(err.message, 'err'));
  }

  mediaItems() {
    const items = [];
    this.props.chatList.forEach(c => {
      const topic = this.props.kaltiq.getTopic(c.topic);
      topic?.messages(msg => {
        if (!Drafty.isValid(msg.content) || !Array.isArray(msg.content.ent)) return;
        msg.content.ent.forEach((ent, index) => {
          if (!ent || !ent.data || !['IM', 'VD', 'AU', 'EX'].includes(ent.tp)) return;
          const url = Drafty.getDownloadUrl(ent.data);
          items.push({topic: c.topic, seq: msg.seq, type: ent.tp, data: ent.data,
            url: url ? this.props.kaltiq.authorizeURL(url) : null, key: `${c.topic}-${msg.seq}-${index}`});
        });
      });
    });
    return items.sort((a, b) => b.seq - a.seq);
  }

  render() {
    const tabs = [
      ['messages', 'chat', 'Messages'], ['calls', 'call', 'Calls'], ['updates', 'auto_awesome', 'Updates'],
      ['channels', 'campaign', 'Channels'], ['groups', 'groups', 'Groups'], ['contacts', 'person', 'Contacts'],
      ['media', 'perm_media', 'Media']
    ];
    const media = this.state.activeTab === 'media' ? this.mediaItems() : [];
    return (
      <div className="kaltiq-home">
        <nav className="kaltiq-home-tabs" aria-label="KalTiQ sections">
          {tabs.map(([id, icon, label]) => <button key={id} className={this.state.activeTab === id ? 'active' : ''}
            onClick={() => this.selectTab(id)} title={label}><i className="material-icons">{icon}</i><span>{label}</span></button>)}
        </nav>
        <div className="kaltiq-list-toolbar">
          {this.state.selecting ? <><b>{this.state.selected.length} selected</b>
            <button onClick={this.deleteSelected} disabled={!this.state.selected.length}><i className="material-icons">delete</i></button>
            <button onClick={() => this.setState({selecting: false, selected: []})}><i className="material-icons">close</i></button></> :
          <><span className="kaltiq-section-name">{tabs.find(t => t[0] === this.state.activeTab)?.[2]}</span>
            <button aria-label="More options" onClick={() => this.setState({menuOpen: !this.state.menuOpen})}><i className="material-icons">more_vert</i></button></>}
          {this.state.menuOpen ? <div className="kaltiq-overflow-menu">
            <button onClick={this.props.onNewGroup}><i className="material-icons">group_add</i>New group</button>
            <button onClick={() => this.setState({selecting: true, selected: [], menuOpen: false})}><i className="material-icons">checklist</i>Select chats</button>
            <button onClick={this.markAllRead}><i className="material-icons">done_all</i>Mark all read</button>
            <button onClick={this.props.onSettings}><i className="material-icons">settings</i>Settings</button>
            <button onClick={this.props.onLogout}><i className="material-icons">logout</i>Log out</button>
          </div> : null}
        </div>
        {this.state.activeTab === 'media' ?
          <div className="kaltiq-media-gallery">{media.length ? media.map(item =>
            <button className="kaltiq-media-card" key={item.key} onClick={() => this.props.onTopicSelected(item.topic)}>
              {item.type === 'IM' && item.url ? <img src={item.url} loading="lazy" /> :
                <i className="material-icons">{item.type === 'VD' ? 'movie' : item.type === 'AU' ? 'graphic_eq' : 'description'}</i>}
              <span>{item.data.name || (item.type === 'IM' ? 'Image' : item.type === 'VD' ? 'Video' : item.type === 'AU' ? 'Audio' : 'File')}</span>
            </button>) : <div className="center-medium-text">Shared media appears here after conversations are loaded.</div>}</div> :
        <FormattedMessage id="contacts_not_found"
        defaultMessage="You have no chats\n¯∖_(ツ)_/¯"
        description="HTML message shown in ContactList when no contacts are found">{
        (no_contacts) => <ContactList
          kaltiq={this.props.kaltiq}
          connected={this.props.connected}
          contacts={this.state.contactList}
          emptyListMessage={no_contacts}
          topicSelected={this.state.selecting ? this.state.selected : this.props.topicSelected}
          myUserId={this.props.myUserId}
          showOnline={true}
          showUnread={true}
          onTopicSelected={this.handleTopicSelected}
          showContextMenu={this.props.showContextMenu}
          onAction={this.handleAction} />
      }</FormattedMessage>}
      </div>
    );
  }
};
